import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiFolder, FiPlay, FiSearch, FiRefreshCw, FiMusic, FiAlertCircle, FiFilter, FiLock } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import Tutorial from './Tutorial';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import { dialogueCategoriesAPI, CategoryDialogue, DialogueCategory, DialogueUserFlag, authAPI, subscriptionAPI } from '../services/api';
import { canUseCategoryDialogue, hasFullLibraryAccess } from '../utils/materialAccess';
import { parseCategoryQueryId } from '../utils/dialogueCategoriesNav';
import MaterialPagination from './MaterialPagination';
import DialogueFlagSelector from './DialogueFlagSelector';
import './DialogueTopicsList.css';
import './DialogueFlagSelector.css';

interface DialogueTopicsListProps {
  onLogout?: () => void;
  userEmail?: string;
}

const DialogueTopicsList: React.FC<DialogueTopicsListProps> = ({ onLogout, userEmail }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCollapsed } = useSidebar();
  const { showTutorial, currentTutorialStep, completeTutorial, skipTutorial } = useTutorial();
  const [dialogues, setDialogues] = useState<CategoryDialogue[]>([]);
  const [completionMap, setCompletionMap] = useState<Record<number, boolean>>({});
  const [categories, setCategories] = useState<DialogueCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedFlags, setSelectedFlags] = useState<DialogueUserFlag[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
    is_staff: false
  });

  useEffect(() => {
    fetchUserProfile();
    checkSubscription();
  }, [userEmail]);

  useEffect(() => {
    if (hasSubscription !== null) {
      fetchCategories();
    }
  }, [hasSubscription, userInfo.is_staff]);

  useEffect(() => {
    if (categories.length > 0) {
      fetchDialogues();
    }
  }, [categories, selectedCategoryId, hasSubscription, userInfo.is_staff, page, searchTerm, selectedFlags]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_staff: profile.is_staff || false
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getSubscriptionStatus();
      const profile = await authAPI.getProfile();
      const isStaff = profile.is_staff || false;
      setHasSubscription(status.has_subscription || isStaff);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasSubscription(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await dialogueCategoriesAPI.getCategories();
      setCategories(data);
      const requested = parseCategoryQueryId(searchParams.get('category'));
      const requestedExists = requested != null && data.some((c) => c.id === requested);
      if (requestedExists) {
        setSelectedCategoryId(requested);
      } else if (data.length > 0 && selectedCategoryId == null) {
        setSelectedCategoryId(data[0].id);
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError(err.message || 'Failed to load categories');
    }
  };

  const fetchDialogues = async () => {
    if (categories.length === 0) {
      setDialogues([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!selectedCategoryId) {
        setDialogues([]);
        setTotalPages(1);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const res = await dialogueCategoriesAPI.getDialogues(selectedCategoryId, {
        page,
        page_size: 24,
        q: searchTerm.trim() || undefined,
        flag: selectedFlags.length > 0 ? selectedFlags.filter(Boolean).join(',') : undefined,
      });
      const data = res.results;
      const pages = res.total_pages;
      const count = res.count;

      setDialogues(data);
      setTotalPages(pages);
      setTotalCount(count);

      const unlockedIds = data
        .filter((t) => canUseCategoryDialogue(hasSubscription, userInfo.is_staff, t.is_free_public))
        .map((t) => t.id);

      const completion: Record<number, boolean> = {};
      if (unlockedIds.length > 0) {
        const batch = await dialogueCategoriesAPI.batchCheckCompletion(unlockedIds);
        for (const [id, done] of Object.entries(batch)) {
          completion[Number(id)] = done;
        }
      }
      for (const t of data) {
        if (!(t.id in completion)) completion[t.id] = false;
      }
      setCompletionMap(completion);
    } catch (err: any) {
      console.error('Error fetching dialogues:', err);
      setError(err.message || 'Failed to load dialogues');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDialogue = (d: CategoryDialogue) => {
    if (!canUseCategoryDialogue(hasSubscription, userInfo.is_staff, d.is_free_public)) {
      window.alert(
        'This dialogue requires an active subscription. Open a dialogue marked as free for everyone, or contact your administrator.'
      );
      return;
    }
    navigate(`/material/dialogues/${d.id}/materials?category=${d.category}`);
  };

  const handleFlagChange = async (dialogueId: number, flag: DialogueUserFlag) => {
    try {
      const result = await dialogueCategoriesAPI.updateUserProgress(dialogueId, flag);
      setDialogues((prev) =>
        prev.map((d) =>
          d.id === dialogueId
            ? { ...d, user_flag: result.user_flag, practice_session_count: result.practice_session_count }
            : d
        )
      );
    } catch (err: any) {
      console.error('Error updating dialogue flag:', err);
      window.alert(err.message || 'Failed to update flag');
    }
  };

  const toggleFlagFilter = (flag: DialogueUserFlag) => {
    if (!flag) return;
    setPage(1);
    setSelectedFlags((prev) =>
      prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag]
    );
  };

  const clearFlagFilters = () => {
    setPage(1);
    setSelectedFlags([]);
  };

  const categoryOptions = categories;

  const libraryDialogueTotal = useMemo(
    () => categories.reduce((sum, category) => sum + (category.dialogues_count ?? 0), 0),
    [categories],
  );

  const filteredDialogues = dialogues;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="dialogue-topics-list-page">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Dialogue Categories</h1>
              <p className="page-description">Select a dialogue to start practicing</p>
            </motion.div>

            {/* Filter and Search Bar */}
            <motion.div
              className="filter-search-container"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Category Filter */}
              <div className="category-filter">
                <div className="filter-label">
                  {React.createElement(FiFilter as React.ComponentType<{ size?: number }>, { size: 18 })}
                  <span>Filter by Category</span>
                </div>
                <div className="category-filter-controls">
                  <select
                    className="category-select"
                    value={selectedCategoryId || ''}
                    onChange={(e) => {
                      setPage(1);
                      const nextId = e.target.value ? parseInt(e.target.value, 10) : null;
                      setSelectedCategoryId(nextId);
                      if (nextId) {
                        setSearchParams({ category: String(nextId) }, { replace: true });
                      }
                    }}
                  >
                    {categoryOptions.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <div className="dialogue-library-total" aria-label="Total dialogues in library">
                    <span className="dialogue-library-total-value">{libraryDialogueTotal}</span>
                    <span className="dialogue-library-total-label">Total Dialogues</span>
                  </div>
                </div>
              </div>

              {/* Search Box */}
              <div className="search-box">
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                <input
                  type="text"
                  placeholder="Search dialogues by name, description, or category..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1);
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>

              {/* Flag Filter */}
              <div className="flag-filter-bar">
                <div className="flag-filter-label">
                  {React.createElement(FiFilter as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span>Filter by flag</span>
                </div>
                <div className="flag-filter-options">
                  {(['red', 'yellow', 'green'] as const).map((flag) => (
                    <button
                      key={flag}
                      type="button"
                      className={`flag-filter-chip ${selectedFlags.includes(flag) ? 'is-active' : ''}`}
                      onClick={() => toggleFlagFilter(flag)}
                    >
                      <span className={`flag-dot flag-${flag}`} />
                      <span>{flag.charAt(0).toUpperCase() + flag.slice(1)}</span>
                    </button>
                  ))}
                  {selectedFlags.length > 0 && (
                    <button type="button" className="flag-filter-clear" onClick={clearFlagFilters}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Subscription Warning */}
            {hasSubscription === false && !userInfo.is_staff && (
              <motion.div
                className="subscription-info-banner"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 20 })}
                <div>
                  <h3>Limited library</h3>
                  <p>
                    You can open dialogues marked <strong>Free</strong> (per dialogue). Subscribe for full access to
                    all dialogues.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
                <button onClick={fetchDialogues} className="retry-btn">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span> Retry</span>
                </button>
              </motion.div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                <p>Loading dialogues...</p>
              </div>
            ) : filteredDialogues.length === 0 ? (
              <div className="empty-state">
                {React.createElement(FiFolder as React.ComponentType<{ size?: number }>, { size: 48 })}
                <p>No dialogues found</p>
                {searchTerm && (
                  <p className="empty-state-hint">Try a different search term</p>
                )}
                {selectedCategoryId && (
                  <p className="empty-state-hint">Try selecting a different category</p>
                )}
              </div>
            ) : (
              <motion.div
                className="topics-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredDialogues.map((d) => {
                  const isCompleted = completionMap[d.id] ?? false;
                  const unlocked = canUseCategoryDialogue(
                    hasSubscription,
                    userInfo.is_staff,
                    d.is_free_public
                  );
                  return (
                  <motion.div
                    key={d.id}
                    className={`topic-card ${isCompleted ? 'topic-completed' : ''} ${!unlocked ? 'topic-card-locked' : ''}`}
                    variants={itemVariants}
                    whileHover={unlocked ? { y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.2)' } : {}}
                    onClick={() => handleSelectDialogue(d)}
                  >
                    <div className="topic-card-header">
                      <div className="topic-icon">
                        {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 24 })}
                      </div>
                      <div className="topic-info">
                        <h3 className="topic-name">{d.name}</h3>
                        <span className="topic-category">{d.category_name}</span>
                        {isCompleted && (
                          <span className="topic-completed-badge">Practiced</span>
                        )}
                        {d.is_free_public && (
                          <span className="topic-free-badge">Free</span>
                        )}
                        {!unlocked && (
                          <span className="topic-lock-badge" title="Subscription required">
                            {React.createElement(FiLock as React.ComponentType<{ size?: number }>, { size: 12 })}
                          </span>
                        )}
                      </div>
                    </div>

                    {d.description && (
                      <p className="topic-description">{d.description}</p>
                    )}

                    <div className="topic-meta">
                      <span className="topic-audio-count">
                        {d.audio_count} {d.audio_count === 1 ? 'audio' : 'audios'}
                      </span>
                      {(d.practice_session_count ?? 0) > 0 && (
                        <span className="topic-practice-count">
                          Practiced {(d.practice_session_count ?? 0)} {(d.practice_session_count ?? 0) === 1 ? 'time' : 'times'}
                        </span>
                      )}
                    </div>

                    <div className="topic-card-flag-row">
                      <DialogueFlagSelector
                        value={d.user_flag ?? null}
                        onChange={(flag) => handleFlagChange(d.id, flag)}
                        disabled={!unlocked}
                        compact
                      />
                    </div>

                    <button
                      className="topic-play-btn"
                      title={isCompleted ? 'Practice this dialogue again' : 'Start practice'}
                    >
                      {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <span> {isCompleted ? 'Practice Again' : 'Start Practice'}</span>
                    </button>
                  </motion.div>
                );
                })}
              </motion.div>
            )}
            {!loading && selectedCategoryId ? (
              <MaterialPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setPage}
              />
            ) : null}
          </div>
        </div>
      </div>
      
      {showTutorial && currentTutorialStep === 3 && (
        <Tutorial
          onComplete={completeTutorial}
          onSkip={skipTutorial}
          initialStep={3}
        />
      )}
    </div>
  );
};

export default DialogueTopicsList;

